//#region Util Imports

import * as _ from 'underscore'

//#endregion

import { ConvertHelper, Dictionary } from '../../../../helpers'
import { SystemService } from '../../../../services/system'
import { OpMarker, OpPolygon, OpPolyline, OpCoord, OpRange, OpBounds, OpTooltip } from '../../../../models/custom/op-map-area'
import { Enums } from '../../../../models/common'


export enum MapEvent {
    Unknown,
    Click
}

export interface MapLegendItem {
    description: string
    color?: string | number
    iconId?: number
    icon?: string
}

export abstract class Map {
    public abstract addMarker(marker: OpMarker): OpMarker
    public abstract setMarkers(markers: OpMarker[], zoom?: boolean, center?: boolean): void
    public abstract setSelectedMarkers(markers: OpMarker[], animate: boolean, zoom?: boolean): void

    public abstract addPolygon(polygon: OpPolygon): OpPolygon
    public abstract setPolygons(polygons: OpPolygon[]): void

    public abstract addRange(range: OpRange): OpRange
    public abstract setRanges(ranges: OpRange[]): void

    public abstract addPolyline(polyline: OpPolyline): OpPolyline

    public abstract computeDistanceBetweenCoords(c1: OpCoord, c2: OpCoord): number

    public abstract zoomCenter(): void
    public abstract zoomIn(): void

    public abstract setCenter(center: OpCoord): void
    public abstract setZoom(zoom: number): void
    public abstract getZoom(): number
    public abstract getBounds(): OpBounds
    public abstract setBounds(bounds: OpBounds): void
    public abstract fitBounds(positions: OpCoord[]): void
    public abstract disableMap(): void
    public abstract enableMap(): void

    public abstract clearListeners(event: MapEvent, marker: OpMarker): void

    public abstract createMarkerTooltip(marker: OpMarker, text: string): OpTooltip

    public abstract setLegend(legendItems: MapLegendItem[]): void

    public abstract resize: () => void
}

export interface ProviderOptions {
    allowMultiTooltipVisibility?: boolean
    clusterZoom: number
    initCenter: OpCoord
    initZoom: number
    isClustering: boolean
    maxZoom: number

    disableMapTypeControl?: boolean
    disableStreetViewControl?: boolean
    defaultMarkerIconOptions?: any
    enableAreaSelect?: boolean
    hideTooltip?: boolean
    initParams?: Dictionary<any>

    onMapClick?: (position: OpCoord) => void
}

export interface ProviderScope extends ng.IScope {
    options: ProviderOptions

    // template options
    showZoomInButton: boolean
}

export abstract class Provider {
    constructor($scope: ProviderScope, private systemService: SystemService) {
        if ($scope.options.allowMultiTooltipVisibility == null || typeof $scope.options.allowMultiTooltipVisibility == 'undefined') {
            $scope.options.allowMultiTooltipVisibility = false;
        }
    }

    // bounds method
    public abstract getBounds(): OpBounds
    public abstract setBounds(bounds: OpBounds): void
    public abstract fitBounds(positions: OpCoord[]): void
    // ---

    // center methods
    public abstract setCenter(center: OpCoord): void

    public abstract center(): void
    public abstract centerOnMarkers(markers: OpMarker[], animate: boolean): void
    // ---

    // zoom methods
    public abstract getZoom(): number
    public abstract setZoom(zoom: number): void

    public abstract zoomCenter(): void
    public abstract zoomIn(): void
    public abstract zoomOnMarkers(markers: OpMarker[], animate: boolean): void
    // ---

    // markers
    public abstract clearMarkers(): void
    public abstract addMarker(marker: OpMarker): OpMarker
    public abstract setMarkers(markers: OpMarker[]): void
    // ---

    // polygons
    public abstract clearPolygons(): void
    public abstract addPolygon(polygon: OpPolygon): OpPolygon
    public abstract setPolygons(polygons: OpPolygon[]): void
    // ---

    // ranges
    public abstract clearRanges(): void
    public abstract addRange(range: OpRange): OpRange
    public abstract setRanges(ranges: OpRange[]): void
    // ---

    // polyline
    public abstract addPolyline(polyline: OpPolyline): OpPolyline
    // ---

    // area select
    public abstract enableAreaSelect(callback: (bounds) => void): void
    public abstract disableAreaSelect(): void
    // ---

    public abstract computeDistanceBetweenCoords(c1: OpCoord, c2: OpCoord): number

    public abstract clearListeners(event: MapEvent, marker: OpMarker): void

    // create marker
    public abstract createMarkerTooltip(marker: OpMarker, text: string): OpTooltip
    // ---

    // resize Map
    public abstract resize(): void
    // ---

    protected checkLatitude(lat: number) {
        return !isNaN(lat) && (+lat >= -85) && (+lat <= 85)
    }

    protected checkLongitude(lon: number) {
        return !isNaN(lon) && (+lon >= -180) && (+lon <= 180)
    }

    protected getMarkerIcon(marker: OpMarker, options: any): any {
        const currentBrowser: Enums.Browsers = this.systemService.getBrowserName()
        if (marker != null) {
            if (marker.iconImage != null) {
                if (marker.iconImage.indexOf('data:image/') > -1) {
                    return marker.iconImage
                }
                if (currentBrowser === Enums.Browsers.InternetExplorer || currentBrowser === Enums.Browsers.MicrosoftEdge) {
                    const extension: string = ConvertHelper.getBase64EncodedFileExtension(marker.iconImage.charAt(0))
                    return 'data:image/' + extension + ';base64,' + marker.iconImage
                }
                return 'data:image/*;base64,' + marker.iconImage
            }

            if (marker.iconURL != null) {
                return marker.iconURL
            }

            let iconConfig: any = null

            if (options.defaultMarkerIconOptions != null) {
                iconConfig = _.clone(options.defaultMarkerIconOptions)
                let color: string = null

                if (marker.fillColor != null) {
                    color = marker.fillColor

                    if (color.charAt(0) === '#') {
                        color = color.replace('#', '').slice(0, 6)
                    }

                    if (color.length > 6) {
                        color = color.slice(0, 6)
                        console.warn('Colors in the RGBA model are not supported!')
                    }
                }
                if (color != null) {
                    iconConfig.fillColor = `#${color}`
                }
            }

            return iconConfig
        }
    }
}